import { Link } from 'react-router-dom';
import { markInk } from '../design/tokens';
import type { Theme } from '../design/tokens';
import type { Folder, Folders } from '../entity/folders';
import { iconFor } from './FolderIcons';
import { folderHref } from './routes';

/**
 * An item's tag as a small mark — its folder's icon in the tag's ink — shown
 * only where what is around the item does not already say the tag.
 *
 * A tag with no icon of its own wears the tag glyph, as the app draws it. On a card, where the glyph alone would not say
 * which tag, it carries the tag's name.
 */
export interface TagMark {
  folder: Folder;
  /** The icon to draw, or null for the default tag glyph. */
  icon: string | null;
  ink: string;
}

const DEFAULT_ICON = 'local_offer';

/** A folder's own mark. */
export function folderMark(folder: Folder, theme: Theme): TagMark {
  return { folder, icon: iconFor(folder.icon) ? folder.icon : null, ink: markInk(theme, folder.hue) };
}

/** The mark an item's [tags] give it: the first that is a folder. Null for
 *  an untagged item, or one whose tags name no folder. */
export function tagMarkOf(tags: readonly string[], folders: Folders, theme: Theme): TagMark | null {
  for (const tag of tags) {
    const folder = folders.byName.get(tag.trim().toLowerCase());
    if (folder) return folderMark(folder, theme);
  }
  return null;
}

/**
 * [mark], unless it would draw the same as [around] — the mark of the page,
 * folder or nook it is shown on. Same by LOOK, not by folder: a subfolder
 * wearing its project's icon says nothing its surroundings have not already
 * said. Two icons are the same when they are one icon; two tag glyphs
 * when they are one hue; an icon and a tag glyph always differ.
 */
export function markWhereDiffers(mark: TagMark | null, around: TagMark | null): TagMark | null {
  if (!mark || !around) return mark;
  const same = mark.icon || around.icon ? mark.icon === around.icon : mark.folder.hue === around.folder.hue;
  return same ? null : mark;
}

/** The icon, or the default tag glyph. Named for a reader only where no name is
 *  written beside it. */
export function TagMarkGlyph({ mark, size, named = false }: { mark: TagMark; size: number; named?: boolean }) {
  const Icon = iconFor(mark.icon ?? DEFAULT_ICON);
  return (
    <span
      className="tag-mark"
      style={{ display: 'inline-flex', flex: 'none', color: mark.ink }}
      {...(named ? {} : { role: 'img', 'aria-label': mark.folder.name, title: mark.folder.name })}
    >
      {Icon && <Icon size={size} />}
    </span>
  );
}

/** On a card or a row: the icon alone, or the tag glyph with the tag's
 *  name. */
export function CardTagMark({ mark, size }: { mark: TagMark; size: number }) {
  if (mark.icon) return <TagMarkGlyph mark={mark} size={size} />;
  return (
    <span
      className="tag-mark-named t-metaMono"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: 'none', color: 'var(--inkFaint)' }}
    >
      <TagMarkGlyph mark={mark} size={12} named />
      {mark.folder.name}
    </span>
  );
}

/** Above an item's title on its own page: the mark and the tag's name, a way
 *  into its folder. */
export function TagCrumb({ mark, className }: { mark: TagMark; className?: string }) {
  return (
    <Link className={`t-eyebrowMono${className ? ` ${className}` : ''}`} to={folderHref(mark.folder.id)}>
      <TagMarkGlyph mark={mark} size={12} named />
      {mark.folder.name}
    </Link>
  );
}
